import React from 'react';
import { AlertTriangle } from 'lucide-react';

/**
 * Small data-hygiene warning pill — surfaces on any UI showing an
 * Offered/Joined application without an `offered_ctc` value. Keeps the
 * visual language consistent across Command Center, Pipeline kanban,
 * Candidates list, and the CtcCleanup admin page.
 *
 * Props:
 *  - size: 'xs' | 'sm' (default 'xs')
 *  - label: optional override (default "CTC pending")
 *  - onClick: optional handler (when present, pill becomes a real button)
 */
const SIZES = {
  xs: 'h-4 px-1 text-[9px] gap-0.5',
  sm: 'h-5 px-1.5 text-[10px] gap-1',
};

const ICON_SIZES = {
  xs: 'w-2.5 h-2.5',
  sm: 'w-3 h-3',
};

const MissingCtcPill = ({
  size = 'xs',
  label = 'CTC pending',
  onClick,
  className = '',
  'data-testid': testId = 'missing-ctc-pill',
}) => {
  const base = `inline-flex items-center rounded font-semibold border border-amber-300 bg-amber-50 text-amber-800 ${SIZES[size] || SIZES.xs} ${className}`;
  const iconSize = ICON_SIZES[size] || ICON_SIZES.xs;
  const title = 'No Offered CTC on file. Joined CTC totals may be understated — click to backfill.';
  if (onClick) {
    return (
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onClick(e);
        }}
        title={title}
        data-testid={testId}
        className={`${base} hover:bg-amber-100 transition cursor-pointer`}
      >
        <AlertTriangle className={iconSize} />
        <span>{label}</span>
      </button>
    );
  }
  return (
    <span
      title={title}
      data-testid={testId}
      className={base}
    >
      <AlertTriangle className={iconSize} />
      <span>{label}</span>
    </span>
  );
};

export default MissingCtcPill;

/**
 * Helper — returns true when an application is in an Offered/Joined stage
 * and its offered_ctc is missing/zero. Centralised so every consumer
 * applies the exact same definition.
 */
export const isMissingOfferedCtc = (app) => {
  if (!app) return false;
  if (app.stage !== 'offer_released' && app.stage !== 'joined') return false;
  const v = app.offered_ctc;
  if (v === null || v === undefined || v === '') return true;
  const n = Number(v);
  return Number.isNaN(n) || n <= 0;
};
